// frontend/src/pages/Settings/Settings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Settings.css';

// Grade-aware topics (must match User.js preferredTopics enum)
const GRADE_8_9_TOPICS = [
  'Whole Numbers', 'Integers', 'Common Fractions', 'Decimal Fractions',
  'Exponents', 'Ratio and Rate', 'Percentages', 'Financial Mathematics',
  'Numeric Patterns', 'Geometric Patterns', 'Algebraic Expressions',
  'Algebraic Equations', 'Graphs', 'Geometry of 2D Shapes',
  'Geometry of 3D Objects', 'Geometric Constructions', 'Transformation Geometry',
  'Area and Perimeter', 'Surface Area and Volume', 'Theorem of Pythagoras',
  'Collect and Organize Data', 'Represent Data', 'Interpret Data', 'Data Handling',
  'Probability'
];

const GRADE_10_12_TOPICS = [
  'Algebra', 'Functions', 'Finance', 'Trigonometry',
  'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
  'Probability', 'Calculus', 'Patterns and Sequences', 'Number Patterns'
];

const getTopicsForGrade = (grade) => {
  const g = parseInt(grade);
  if (g >= 8 && g <= 9) return GRADE_8_9_TOPICS;
  return GRADE_10_12_TOPICS;
};

const Settings = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    bio: '',
    grade: '',
    preferredTopics: []
  });
  
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        bio: user.profile?.bio || '',
        grade: user.profile?.grade || 10,
        preferredTopics: user.profile?.preferredTopics || []
      });
    }
  }, [user]);

  // Get topics based on current grade selection
  const availableTopics = getTopicsForGrade(formData.grade);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle grade change — clear invalid topics when switching grade bands
  const handleGradeChange = (e) => {
    const newGrade = parseInt(e.target.value);
    const newTopics = getTopicsForGrade(newGrade);
    const validTopics = formData.preferredTopics.filter(t => newTopics.includes(t));
    setFormData(prev => ({
      ...prev,
      grade: newGrade,
      preferredTopics: validTopics
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTopicToggle = (topic) => {
    setFormData(prev => ({
      ...prev,
      preferredTopics: prev.preferredTopics.includes(topic)
        ? prev.preferredTopics.filter(t => t !== topic)
        : [...prev.preferredTopics, topic]
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        'profile.name': formData.username,
        'profile.bio': formData.bio,
        'profile.grade': parseInt(formData.grade),
        'profile.preferredTopics': formData.preferredTopics
      };

      const response = await api.put('/api/profile/update', updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update AuthContext so all components reflect the changes
      if (user) {
        updateUser({
          ...user,
          profile: {
            ...user.profile,
            bio: formData.bio,
            grade: parseInt(formData.grade),
            preferredTopics: formData.preferredTopics
          }
        });
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.response?.data?.message || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/api/auth/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to change password' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete('/api/users/account');
      logout();
      navigate('/');
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to delete account' 
      });
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button 
          className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          Preferences
        </button>
        <button 
          className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => navigate('/settings/subscription')}
        >
          Subscription
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-content">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="settings-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="grade">Grade</label>
              <select
                id="grade"
                name="grade"
                value={formData.grade}
                onChange={handleGradeChange}
                required
              >
                <option value="">Select Grade</option>
                <option value="8">Grade 8</option>
                <option value="9">Grade 9</option>
                <option value="10">Grade 10</option>
                <option value="11">Grade 11</option>
                <option value="12">Grade 12</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}

        {activeTab === 'security' && (
          <div className="security-section">
            <form onSubmit={handlePasswordSubmit} className="settings-form">
              <h3>Change Password</h3>
              
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwords.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwords.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>

            <div className="danger-zone">
              <h3>Danger Zone</h3>
              <p>Once you delete your account, there is no going back.</p>
              <button 
                className="btn btn-danger" 
                onClick={handleDeleteAccount}
              >
                Delete Account
              </button>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="preferences-section">
            <h3>Preferred Topics</h3>
            <p>Select topics you're interested in for personalised recommendations (showing topics for Grade {formData.grade || '...'}).</p>
            
            <div className="topics-grid">
              {availableTopics.map(topic => (
                <label key={topic} className="topic-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.preferredTopics.includes(topic)}
                    onChange={() => handleTopicToggle(topic)}
                  />
                  <span>{topic}</span>
                </label>
              ))}
            </div>

            <button 
              className="btn btn-primary" 
              onClick={handleProfileSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
